import ReactECharts from 'echarts-for-react';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Briefcase,
  ChevronRight,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import { toast } from '../../../../ui/toast';
import { Applicant, TabData } from '../../../../utils/types/JobOpenings';

export interface SegmentModalProps {
  segmentedCandidates: Applicant[];
  xCoords: number[];
  yCoords: number[];
  selectedCandidates: TabData[];
  setSelectedCandidates: (tabDataObject: TabData[]) => void | undefined;
  setIsModalOpen: (content: boolean) => void;
  setTabTitles: (titles: string[]) => void | undefined;
  setActiveTab: (value: number) => void;
  tabTitles: string[];
  useSkillPercentage?: boolean;
  onCandidateClick?: (
    candidate: Applicant,
    tileCandidates?: Applicant[],
    tileName?: string
  ) => void;
  currentTab?: string;
  isApplicantSelected?: (applicant: Applicant) => boolean;
  filteredSkills?: string[];
}

export const SegmentModal = ({
  segmentedCandidates,
  xCoords,
  yCoords,
  selectedCandidates,
  setSelectedCandidates,
  setTabTitles,
  setActiveTab,
  tabTitles,
  useSkillPercentage,
  onCandidateClick,
  isApplicantSelected,
  filteredSkills,
}: SegmentModalProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [candidates, setCandidates] =
    useState<Applicant[]>(segmentedCandidates);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const active = document.activeElement as HTMLElement | null;
    if (
      active &&
      (active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.isContentEditable)
    ) {
      return;
    }

    if (event.key === 'Backspace') {
      setSearchTerm((prev) => prev.slice(0, -1));
    } else if (/^[a-zA-Z\s]$/.test(event.key)) {
      const key = event.key.toLowerCase();
      setSearchTerm((prev) => prev + key);
      setCurrentPage(1);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const filtered = segmentedCandidates.filter((candidate) =>
      candidate.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setCandidates(filtered);
    setCurrentPage(1);
  }, [searchTerm, segmentedCandidates]);

  const itemsPerPage = 7;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = candidates.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(candidates.length / itemsPerPage);

  const openTab = (name: string, userId: string) => {
    const candidate = candidates.find((item) => item.userId === userId);

    if (onCandidateClick && candidate) {
      onCandidateClick(candidate);
      return;
    }

    const nameExists = selectedCandidates.some(
      (item) => item.candidate && item.candidate.userId === userId
    );
    if (!nameExists) {
      setTabTitles([...tabTitles, name]);
      const selectedItem: Applicant | undefined = candidates.find(
        (item) => item.userId === userId
      );
      const newSelectedCandidates = [
        ...selectedCandidates,
        { tabId: tabTitles.length, candidate: selectedItem },
      ];
      setSelectedCandidates(newSelectedCandidates);
      setActiveTab(tabTitles.length);
    } else {
      toast.error('Tab for ' + name + ' already open', {
        position: 'bottom-center',
      });
    }
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    return parts.length > 1
      ? parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase()
      : parts[0]?.[0]?.toUpperCase() || 'NA';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'Unsuccessful-Closed': '#dc2626',
      'Unsuccessful-Pending': '#ea580c',
      Suitable: '#2563eb',
      Outstanding: '#9333ea',
      'Auto-Assessment': '#d97706',
      Shortlisted: '#059669',
      New: '#0891b2',
    };
    return colors[status] || '#64748b';
  };

  const renderCandidateAvatar = (item: Applicant) => {
    const initials = getInitials(item.name || `${item.firstName} ${item.lastName}`);
    const isSelected = isApplicantSelected && isApplicantSelected(item);
    const applicantStatus = item.applicantStatus;

    if (applicantStatus === 'Unsuccessful-Closed') {
      return (
        <Avatar
          sx={{
            bgcolor: '#dc2626',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '13px',
            width: 38,
            height: 38,
            boxShadow: '0 2px 4px rgba(220, 38, 38, 0.25)',
          }}
        >
          {initials}
        </Avatar>
      );
    }
    if (applicantStatus === 'Unsuccessful-Pending' && isSelected) {
      return (
        <Avatar
          sx={{
            bgcolor: '#eab308',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '13px',
            width: 38,
            height: 38,
            boxShadow: '0 2px 4px rgba(234, 179, 8, 0.25)',
          }}
        >
          {initials}
        </Avatar>
      );
    }
    if (applicantStatus === 'Unsuccessful-Pending' && !isSelected) {
      return (
        <Avatar
          sx={{
            bgcolor: '#f97316',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '13px',
            width: 38,
            height: 38,
            boxShadow: '0 2px 4px rgba(249, 115, 22, 0.25)',
          }}
        >
          {initials}
        </Avatar>
      );
    }

    if (item.symbol && item.symbol !== 'circle') {
      return (
        <Avatar
          src={item.symbol.replace('image://', '')}
          alt={item.name}
          sx={{ width: 38, height: 38, boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}
        />
      );
    }

    return (
      <Avatar
        sx={{
          bgcolor: 'rgba(37, 99, 235, 0.1)',
          color: '#2563eb',
          fontWeight: 700,
          fontSize: '13px',
          width: 38,
          height: 38,
          border: '1px solid rgba(37, 99, 235, 0.2)',
        }}
      >
        {initials}
      </Avatar>
    );
  };

  const getOption = () => ({
    title: {
      text: `Number of Candidates: ${candidates.length}`,
      left: 'center',
      top: 10,
      textStyle: {
        fontSize: 13,
        fontWeight: 600,
        color: '#475569',
        fontFamily: 'Inter, system-ui, sans-serif',
      },
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: 'rgba(0, 0, 0, 0.05)',
      borderWidth: 0,
      confine: true,
      position: function (
        point: [number, number],
        _params: unknown,
        dom: HTMLElement,
        _rect: unknown,
        size: { viewSize: [number, number] }
      ) {
        const viewWidth = size.viewSize[0];
        const viewHeight = size.viewSize[1];
        const tooltipWidth = dom.offsetWidth;
        const tooltipHeight = dom.offsetHeight;
        let [x, y] = point;

        if (x + tooltipWidth > viewWidth) {
          x = x - tooltipWidth - 10;
        } else {
          x = x + 10;
        }

        if (y + tooltipHeight > viewHeight) {
          y = y - tooltipHeight - 10;
        } else {
          y = y + 10;
        }

        return [
          Math.max(0, Math.min(x, viewWidth - tooltipWidth)),
          Math.max(0, Math.min(y, viewHeight - tooltipHeight)),
        ];
      },
      extraCssText:
        'box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); backdrop-filter: blur(8px); border-radius: 8px; z-index: 1000; pointer-events: none;',
      padding: [12, 16],
      textStyle: {
        color: '#333',
        fontFamily: 'Inter, Segoe UI, sans-serif',
        fontSize: 13,
      },
      formatter: (params: { data: Applicant & { noOfSkills?: number; experience?: number } }) => {
        const { data } = params;
        const matchedSkillsArray = Array.isArray(data?.matchedSkills)
          ? data.matchedSkills
          : [];

        const displayedSkills =
          filteredSkills && filteredSkills.length > 0
            ? matchedSkillsArray.filter((skill: string) =>
                filteredSkills.some(
                  (fs: string) => fs.toLowerCase() === skill.toLowerCase()
                )
              )
            : matchedSkillsArray;

        const matchedSkills =
          displayedSkills.length > 0 ? displayedSkills.join(', ') : 'N/A';

        return `
          <span style="display: block; font-family: 'Inter', sans-serif; font-size: 13px; color: #333; line-height: 1.6; max-width: 280px;">
            <span style="display: block; font-size: 15px; font-weight: 600; color: #0f172a; margin-bottom: 8px; border-bottom: 1px solid #f1f5f9; padding-bottom: 6px;">
              ${data?.name || 'Unknown'}
            </span>
            <span style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span style="color: #64748b;">Experience</span>
              <span style="font-weight: 600; color: #2563eb;">${data?.experience ?? 'N/A'} yrs</span>
            </span>
            <span style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span style="color: #64748b;">Skills Match</span>
              <span style="font-weight: 600; color: #2563eb;">${data?.noOfSkills ?? 'N/A'}</span>
            </span>
            <span style="display: block; border-top: 1px solid #f1f5f9; margin-top: 8px; padding-top: 6px;">
              <span style="display: block; color: #64748b; font-size: 11px; margin-bottom: 2px;">Matched Skills</span>
              <span style="display: -webkit-box; font-weight: 500; color: #2563eb; line-height: 1.4; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis;">
                ${matchedSkills}
              </span>
            </span>
          </span>
        `;
      },
    },
    xAxis: {
      name: 'Number of Skills',
      min: xCoords[0].toFixed(1),
      max: xCoords[1].toFixed(1),
      splitLine: { show: true, lineStyle: { color: '#f1f5f9' } },
      nameLocation: 'middle',
      nameGap: 25,
      nameTextStyle: { color: '#64748b', fontSize: 11, fontWeight: 500 },
    },
    yAxis: {
      name: 'Experience (Years)',
      min: yCoords[0].toFixed(1),
      max: yCoords[1].toFixed(1),
      splitLine: { show: true, lineStyle: { color: '#f1f5f9' } },
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: '#64748b', fontSize: 11, fontWeight: 500 },
    },
    series: [
      {
        symbolSize: 36,
        data: candidates.map((candidate) => ({
          ...candidate,
          name: candidate.name,
          noOfSkills: candidate.value[0],
          experience: candidate.value[1],
          matchedSkills: candidate.matchedSkills,
        })),
        type: 'scatter',
        itemStyle: {
          color: (params: { data: { candidateStatus?: string } }) => {
            const status = params.data.candidateStatus || 'default';
            return getStatusColor(status);
          },
        },
        label: {
          show: true,
          position: 'inside',
          fontWeight: 'bold',
          fontSize: 11,
          color: '#ffffff',
          formatter: (params: { data: { name?: string } }) => {
            const name = params.data.name || '';
            return getInitials(name);
          },
        },
      },
    ],
  });

  const onChartClick = (param: { componentType?: string; data?: Applicant }) => {
    if (param.data && param.data.name) {
      if (param.componentType === 'series' && param.data) {
        const selectedItem: Applicant | undefined = candidates.find(
          (item) => item.userId === param.data?.userId
        );

        if (onCandidateClick && selectedItem) {
          onCandidateClick(selectedItem);
          return;
        }

        const nameExists = selectedCandidates.some(
          (item) =>
            item.candidate && item.candidate.userId === param.data?.userId
        );
        if (!nameExists) {
          setTabTitles([...tabTitles, param.data.name]);
          const newSelectedCandidates = [
            ...selectedCandidates,
            { tabId: tabTitles.length, candidate: selectedItem },
          ];
          setSelectedCandidates(newSelectedCandidates);
          setActiveTab(tabTitles.length);
        } else {
          toast.error('Tab for ' + param.data.name + ' already open', {
            position: 'bottom-center',
          });
        }
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        height: '100%',
        minHeight: 0,
        flex: 1,
        overflow: 'hidden',
      }}
    >
      {/* Left Column: ECharts Scatter Chart */}
      <Paper
        elevation={0}
        sx={{
          flex: { xs: '1 1 360px', md: '7' },
          minHeight: 0,
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, width: '100%', height: '100%' }}>
          <ReactECharts
            option={getOption()}
            style={{ height: '100%', width: '100%' }}
            onEvents={{ click: onChartClick }}
            opts={{ renderer: 'canvas' }}
          />
        </Box>
      </Paper>

      {/* Right Column: Candidates Search & Scrollable List */}
      <Paper
        elevation={0}
        sx={{
          flex: { xs: '1 1 360px', md: '5' },
          minHeight: 0,
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'grey.50',
          overflow: 'hidden',
        }}
      >
        {/* Fixed Header: Search input & counts */}
        <Box
          sx={{
            p: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Type to search candidates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} color="#64748b" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                  >
                    <X size={14} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: '8px',
                bgcolor: 'grey.50',
                fontSize: '13px',
                height: '36px',
              },
            }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '12px' }}
            >
              {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} found
            </Typography>
            {searchTerm && (
              <Chip
                label="Filtered"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '20px', fontSize: '10.5px' }}
              />
            )}
          </Stack>
        </Box>

        {/* Scrollable Mid-Content: Candidates List */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(0, 0, 0, 0.15)',
              borderRadius: '3px',
            },
          }}
        >
          {currentItems.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                py: 6,
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                No candidates match your search
              </Typography>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                Try clearing or changing your search terms
              </Typography>
            </Box>
          ) : (
            currentItems.map((item) => {
              const fullName = item.name || `${item.firstName} ${item.lastName}`;
              const skillsCount = useSkillPercentage
                ? `${Number(item.value?.[0] ?? 0).toFixed(1)}%`
                : `${Number(item.value?.[0] ?? item.noOfSkills ?? 0)}`;
              const experience = Number(item.value?.[1] ?? item.experience ?? 0).toFixed(1);

              return (
                <Card
                  key={item.userId}
                  elevation={0}
                  onClick={() => openTab(fullName, item.userId)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '10px',
                    p: 1.25,
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    {renderCandidateAvatar(item)}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{
                          fontWeight: 600,
                          fontSize: '13px',
                          color: 'text.primary',
                        }}
                      >
                        {fullName}
                      </Typography>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: 'text.secondary',
                            fontSize: '11.5px',
                          }}
                        >
                          <Sparkles size={12} color="#2563eb" />
                          <strong style={{ color: '#0f172a' }}>{skillsCount}</strong> Skills
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: 'text.secondary',
                            fontSize: '11.5px',
                          }}
                        >
                          <Briefcase size={12} color="#64748b" />
                          <strong style={{ color: '#0f172a' }}>{experience}</strong> yrs
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>

                  <Tooltip title="View Profile">
                    <IconButton
                      size="small"
                      sx={{
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: '6px',
                        p: 0.5,
                        color: 'text.secondary',
                        '&:hover': {
                          bgcolor: 'primary.50',
                          color: 'primary.main',
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <ChevronRight size={14} />
                    </IconButton>
                  </Tooltip>
                </Card>
              );
            })
          )}
        </Box>

        {/* Fixed Footer: Pagination */}
        <Box
          sx={{
            p: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {totalPages > 1 ? (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              size="small"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 600,
                  fontSize: '11.5px',
                  borderRadius: '6px',
                  minWidth: '28px',
                  height: '28px',
                },
              }}
            />
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11.5px' }}>
              Showing all candidates
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
